import { useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link as RouterLink,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { useSession } from './auth';
import { rememberName, rememberSession } from './session-hint';
import { AccountProvider, Link, LinkProvider, type LinkProps } from './ui';
import { audienceForPath, isAppPath, resolveSite, type SiteId } from '@goodbuild/rules/sites';
import { displayName } from '@goodbuild/rules/display-name';
import { useApi } from './api';
import { applySpaceIcons } from './space-icons';
import { AccountRoute } from './routes/account';
import { BoughtRoute } from './routes/bought';
import { LibraryRoute } from './routes/library';
import { OrdersRoute } from './routes/orders';
import { BoardRoute } from './routes/board';
import { OnboardingRoute } from './routes/onboarding';
import { AdminRoute } from './routes/admin';
import { SignInRoute } from './routes/sign-in';
import { SignUpRoute } from './routes/sign-up';
import {
  ForgotPasswordRoute,
  ResetPasswordRoute,
  VerifiedRoute,
  VerifyEmailRoute,
} from './routes/password';
import { AdminGamesRoute } from './routes/admin-games';
import { AdminGameRoute } from './routes/admin-game';
import { BrandRoute } from './routes/brand';
import { AdminNewTitleRoute } from './routes/admin-new-title';
import { AdminPeopleRoute, AdminPersonRoute } from './routes/admin-people';
import { AdminTokensRoute } from './routes/admin-tokens';
import { AdminGiftsRoute } from './routes/admin-gifts';
import { CreatorQueueRoute } from './routes/admin-queues';
import { BrowseRoute } from './routes/browse';
import { GameRoute } from './routes/game';
import { CreatorPageRoute } from './routes/profiles';
import { AboutRoute } from './routes/about';
import { ContactRoute } from './routes/contact';
import { CreatorsRoute } from './routes/creators';
import { DownloadRoute } from './routes/download';
import { LegalRoute } from './routes/legal';
import { NotFound } from './routes/shared';
import { isLauncher } from './is-launcher';
import { isRegistered } from './launcher-device';
import { useWorkRail } from './rail';
import { Navigating, Waiting } from './loading';
import { DownloadsRoute } from './routes/downloads';
import { LibraryGameRoute } from './routes/library-game';
import { RefundQueueRoute } from './routes/admin-refunds';
import { LauncherSettingsRoute } from './routes/launcher-settings';

/**
 * Which site this app is standing in for.
 *
 * The three hosts still mean three different things — creators.goodbuild.cc
 * and developers.goodbuild.cc have their own navigation, their own footer and
 * their own ground colour — and that mapping already exists in
 * `@goodbuild/rules/sites`.
 *
 * **This is now the only deployment there is.** `goodbuild.cc` was the Astro
 * storefront and this app was the three dashboard hosts; the storefront is gone
 * and this bundle answers on the apex too. So `resolveSite` answers `store` for
 * the apex and for `app.goodbuild.cc`, which is the same answer it always gave
 * and now covers the public pages as well.
 */
function currentSite(): SiteId {
  if (typeof window === 'undefined') return 'store';
  return resolveSite(window.location.hostname) ?? 'store';
}

/**
 * Whose colours this page is painted in.
 *
 * Not the same question as `currentSite`, and conflating the two is why a
 * studio standing in its own portal was looking at the players' ground. This
 * app is one deployment on `app.goodbuild.cc`, so `resolveSite` answers `store`
 * for every page in it — the portal, the board and a player's library all came
 * out warm and orange, and section 04's whole claim is that a person should
 * know which side of the product they are on before reading a word.
 *
 * The path knows. `/portal` is a studio's workplace and `/board` is a
 * creator's, wherever they are served from, and `audienceForPath` is that
 * mapping — kept in `apps/api` beside `resolveSite` rather than here,
 * because it is the same fact about the same product and the storefront will
 * eventually want to ask it too.
 *
 * The host is the fallback rather than the answer, for the paths that belong to
 * no single audience: `/account` is whoever is signed in, `/admin` is staff.
 * On `developers.goodbuild.cc` those still come out in Build, which is right —
 * somebody who arrived on that host is a studio whatever page they open.
 */
function currentSpace(path?: string): SiteId {
  const here = path ?? (typeof window === 'undefined' ? '/' : window.location.pathname);

  /*
   * The vanity creator URL, which no prefix table can express.
   *
   * `AUDIENCE_PATHS` matches a prefix exactly or followed by a slash, and
   * `/@victor` is neither `/@` nor `/@/…`. It is the same page as `/c/victor`,
   * which *is* in that table, so answering differently here would give one of
   * two URLs for one page the wrong favicon and the wrong ground.
   */
  if (here.startsWith('/@')) return 'creators';

  return audienceForPath(here) ?? currentSite();
}

/**
 * TanStack's Link, adapted to the shape apps/app/src/ui renders.
 *
 * `to` is a union of this app's own routes; the library's `href` is a plain
 * string, because it is shared with two applications whose route maps differ.
 * The cast is the one place that boundary is crossed, and a wrong href fails
 * at the router rather than silently rendering a dead link.
 *
 * **The query string is split off and handed over separately**, because
 * TanStack matches `to` against route paths and `/portal?org=…` is not one:
 * left whole it does not resolve, and the link goes nowhere. Everything in the
 * chrome was a bare path until the rail grew an organisation switcher (#88),
 * which is why this held for as long as it did. The hash goes the same way for
 * the same reason — nothing draws one today, and the next thing that does
 * should not have to discover this.
 */
function splitHref(href: string): {
  path: string;
  search: Record<string, string> | undefined;
  hash: string | undefined;
} {
  const hashAt = href.indexOf('#');
  const hash = hashAt < 0 ? undefined : href.slice(hashAt + 1);
  const withoutHash = hashAt < 0 ? href : href.slice(0, hashAt);

  const queryAt = withoutHash.indexOf('?');
  if (queryAt < 0) return { path: withoutHash, search: undefined, hash };

  const search: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(withoutHash.slice(queryAt + 1))) {
    search[key] = value;
  }

  return { path: withoutHash.slice(0, queryAt), search, hash };
}

/*
 * Every prop this adapter does not name is a prop that silently does nothing.
 *
 * That is not hypothetical: the store capsule's tilt handlers are passed to
 * `Link`, and a link rendered through here would have dropped them — a shelf of
 * cards that no longer turn, with nothing failing anywhere. `LinkProps` is a
 * closed interface for exactly this reason, so adding one there is what forces
 * the question here.
 */
const RouterLinkAdapter = ({
  href,
  children,
  className,
  style,
  title,
  ref,
  onPointerMove,
  onPointerLeave,
  onPointerEnter,
  onPointerDown,
  ...rest
}: LinkProps) => {
  const { path, search, hash } = splitHref(href);

  return (
    <RouterLink
      to={path as never}
      {...(search ? { search: search as never } : {})}
      {...(hash ? { hash } : {})}
      className={className}
      style={style}
      title={title}
      ref={ref as never}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onPointerEnter={onPointerEnter}
      onPointerDown={onPointerDown}
      {...(rest['data-tilt'] === undefined ? {} : { 'data-tilt': rest['data-tilt'] })}
    >
      {children}
    </RouterLink>
  );
};

/**
 * The account cluster in the chrome: who you are, and the way to your account.
 *
 * **It used to be a bare `Sign out` button**, which made the only thing you
 * could do with your account leaving it — and put the most destructive action
 * available one stray click from every page, immediately beside links people
 * use constantly. It also had nowhere to say what it signed out *of*, so a
 * player who had lost a laptop had no reason to think the launcher on it kept
 * its own token.
 *
 * A link to `/account` instead. Signing out lives there, under a sentence, next
 * to the list of machines that is the answer to the lost-laptop case.
 *
 * The name rather than the word "Account": on a shared machine, which of two
 * people is signed in is the thing worth reading, and the bar renders it in the
 * same mono label voice as everything else in it.
 *
 * **The caller normalises the name and this takes what survives** (#113). It
 * used to be handed `session?.user.name ?? 'Account'`, and `??` catches only
 * null and undefined — an account whose name is the empty string, or a space,
 * rendered an anchor with no text in it: a link in the bar that nobody can see
 * and nobody can click, and no way to tell from the page that anything was
 * wrong. `displayName` in `Shell` is where that is decided now, once, for this
 * and for the cookie the storefront reads, so the two surfaces cannot come to
 * different conclusions about what somebody is called.
 */
function Account({ name }: { name: string }): React.ReactElement {
  // `Link` rather than `RouterLink`: this renders inside `LinkProvider`, so it
  // resolves to the adapter above — which is where the one cast between the
  // library's string `href` and TanStack's route union already lives. A second
  // `as never` here would be a second place to get that wrong.
  return <Link href="/account">{name}</Link>;
}

/**
 * The four ways in, and the one page reached by somebody who cannot sign in.
 *
 * **This list used to be the whole of what rendered without a session**, under
 * the name `PUBLIC_PATHS`, because every other route in this app was a
 * dashboard. That inverted when the storefront's pages moved here: most of the
 * application is public now — the catalogue, a store page, the pitch pages, the
 * legal documents — and a list of what does *not* need a session would be most
 * of the router, maintained by hand, failing silently whenever somebody forgot
 * a line.
 *
 * So the gate asks `isAppPath` instead, which is the closed list in
 * `@goodbuild/rules/sites` of what belongs to the signed-in surface. These
 * five are its documented exception: they are on that list because they are
 * this application's rather than the shop's, and they cannot be behind a
 * session — a sign-in page behind a sign-in is the one page that must never
 * require one.
 *
 * `/verified` is here for a subtler reason. Verification does sign somebody in,
 * but the session is established by the redirect that lands on this route, so
 * for one render `useSession` has not caught up.
 */
/**
 * The half of `/me` the chrome reads.
 *
 * Declared here rather than shared with the account page's `Me`, which reads
 * the studios, the creator context and the rest. This is the standing and
 * nothing else, so a change to any of those cannot silently widen what the
 * navigation depends on.
 */
interface MeStanding {
  /*
    `developer` was the third standing and `studios` was beside it, holding
    every organisation the account could act for so the rail could name them
    (#88). Both went with the studios (#144).
  */
  standing: { creator: string; admin: string };
}

const AUTH_PATHS = new Set([
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verified',
  /*
    The wall, which is exempt from the gate below for the same reason `/verified`
    is: it is a page about a session being established rather than a page that
    reads one. It is also the one place `walled` must not fire — a screen that
    replaced itself would be a screen nobody could read.
  */
  '/verify-email',
]);

/**
 * Whether this path may only be read by somebody signed in.
 *
 * One question, asked in one place, from the same closed list that decides
 * which surface a chrome link belongs to. A route that is genuinely public —
 * `/browse`, `/g/tiny-city`, `/legal/terms` — is simply not on it, so adding a
 * public page needs nothing here; a route that is added to `APP_PATHS` is
 * gated by that fact alone.
 */
function needsSession(path: string): boolean {
  return isAppPath(path) && !AUTH_PATHS.has(path);
}

function Shell(): React.ReactElement {
  const { data: session, isPending } = useSession();
  const signedIn = Boolean(session?.user) || isRegistered();

  /**
   * The tab, in the colours of the page it is showing.
   *
   * Subscribed to the router's location rather than read off `window`, because
   * this is the one thing here that has to run *again* on a navigation. The
   * gate below reads `window.location` and is correct doing so — it only needs
   * to be right for the render it is in — whereas an icon left behind is wrong
   * until the next full page load, which in a single-page app may be never.
   *
   * `currentSpace` is given the path explicitly for the same reason: during the
   * render that follows a navigation, `window.location.pathname` and the
   * router's idea of where we are do agree, but relying on that is relying on
   * an ordering nobody wrote down.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    applySpaceIcons(currentSpace(pathname));
  }, [pathname]);

  /**
   * Which of the three role links the top bar may draw.
   *
   * The same `/me` half the account page reads, under the same query key — so
   * this is one request per session rather than one per page, and it is the
   * request sixteen routes were making anyway. `enabled` keeps it from firing
   * for a signed-out visitor, who has no roles and no session to ask with.
   *
   * `active` and nothing else. `standingFor` distinguishes applied from active
   * from suspended because the account page has to be able to say which; a bar
   * has one question per role, and an applied creator cannot open the board.
   * They read why on the account page rather than by pressing a link that
   * refuses them — see section 3 on why the board is closed until approval.
   */
  const me = useApi<MeStanding>(['me'], '/me', { enabled: signedIn && !isPending });

  /*
   * Pulled out one per role rather than built inline, because two things read
   * them now: the bar on this origin, and the effect below that writes them
   * into the storefront's hint. An effect cannot depend on the object — it is a
   * new one every render — so the booleans have to exist on their own.
   */
  const creator = me.data?.standing.creator === 'active';
  const admin = me.data?.standing.admin === 'active';

  /*
   * `launcher` is beside the three roles and is not one of them.
   *
   * The roles come from `/me` and describe the account; this describes the
   * program asking, and the same account is in the launcher on one machine and
   * a browser on another at the same moment. So it is read here rather than
   * fetched, and it is deliberately *not* part of what `rememberSession` writes
   * into the hint below — the storefront would then offer `Downloads` to a web
   * player whose browser has no bridge to call. See `NavStanding.launcher`.
   */
  const standing = { signedIn, creator, admin, launcher: isLauncher() };

  /**
   * What to call this person, decided once for both surfaces (#113).
   *
   * Two things read it and they must not disagree: the bar on this origin,
   * which renders it directly, and the `gb_name` cookie the storefront draws
   * *its* bar from. Normalising at each of them separately would be two rules
   * for one name, and the failure that produces is a person greeted one way on
   * the shop and another way in their own portal.
   *
   * `displayName` rather than `?? 'Account'`, which is the smaller bug in the
   * same place: `??` catches null and undefined and lets the empty string
   * through, so an account with no name rendered a link with no label. See
   * `@goodbuild/rules/display-name` for the whole of what counts as a name.
   *
   * A string and not an object, so the effect below can depend on it: a value
   * React can compare by identity is the difference between writing a cookie
   * when the name changes and writing one on every render.
   */
  const name = displayName(session?.user.name);

  /**
   * Whether the three booleans above mean anything yet.
   *
   * `me.data` is undefined both while the request is out and after it fails, so
   * the standing reads as "no roles" in a state where the truthful answer is
   * "we have not been told". That is the right thing for the bar on this
   * origin, which is redrawn the instant `/me` lands — and the wrong thing to
   * write into a cookie that another origin will read for a week.
   *
   * Settled rather than successful, deliberately. A `/me` that fails leaves the
   * roles unknown for as long as it keeps failing, and never writing the hint
   * would be worse than writing a bar with no role links in it: the player
   * would be offered `Sign in` on the storefront while sitting inside their own
   * account. So a failure writes what it honestly knows — signed in, no roles —
   * and the account page is one click from every store page either way.
   */
  const rolesSettled = me.isSuccess || me.isError;

  /**
   * Which organisation the portal is about, and what the rail says because of it.
   *
   * Here rather than in the portal, because the rail is drawn by `Page` on every
   * screen under `/portal` — statistics, builds, the submission form — and each
   * of them would otherwise have to work the answer out for itself and hand it
   * up. See `rail.ts` for the two-state rule and why an empty list is how "draw
   * nothing" is said.
   *
   * `admin` goes in beside `creator` for #111, and it is the same boolean the
   * top bar's `Admin` entry is drawn from a few lines above — one reading of
   * `/me`, one threshold, so the bar and the rail cannot come to disagree about
   * whether somebody is staff.
   */
  const rail = useWorkRail({ creator, admin, known: rolesSettled });

  /**
   * Tell the storefront, which has no way of finding out for itself.
   *
   * Here rather than in the sign-in form, and that is the point: this runs once
   * the session has actually been *checked*, so it covers every way in and
   * every way out at once — a password sign-in, a social popup, an email
   * verification link, a sign-out, and a token that quietly expired between
   * visits. Writing it from each of those instead would be five places, and the
   * expiry has no form to hang it off.
   *
   * Waits for `isPending` to clear. During the check we do not know, and
   * writing "signed out" into that gap would blank a correct hint on every page
   * load, which is worse than being a moment late.
   *
   * **`rolesSettled` is the same argument one layer in, and #62 is why it had
   * to be added.** The hint carries the role links now, so the gap between
   * "there is a session" and "`/me` has said what it can open" is a second gap
   * with a wrong answer sitting in it: a creator who reloads this app would
   * have had `1cda` overwritten with a bare `1` for as long as the request took,
   * and a store page loaded in that window would draw a bar missing the two
   * links they come back for. Being a moment late is fine. Being wrong is not,
   * and a hint lives for a week.
   *
   * A sign-out does not wait for any of it. `signedIn` false is complete on its
   * own, there is nothing left to ask `/me` about, and the one state that must
   * never be slow to propagate is the one that takes links away.
   *
   * The three role booleans are listed rather than `standing`, which is a fresh
   * object every render and would make this an effect that runs on every
   * render — that is, an assignment to `document.cookie` on every keystroke of
   * every form in the application. `name` is a string for the same reason and
   * is listed for the same reason: a rename on the account page has to reach
   * the storefront, and nothing else may.
   *
   * **The name is written and cleared in the same breath as the hint** (#113),
   * beside each of the two calls rather than in a step of its own. They are two
   * cookies describing one moment: a sign-out clears both here, and a signed-in
   * render writes both here, so there is no ordering in which the storefront
   * can hold one without the other for longer than this function takes. The
   * store guards the same seam from its end by refusing to read the name unless
   * the hint says somebody is signed in — see `lib/display-name`, which argues
   * why a name gets a cookie of its own and why nothing may believe it.
   *
   * The name waits on `rolesSettled` with the hint, which is a moment later
   * than it strictly needs to be — the name comes from the session and is known
   * as soon as `isPending` clears. Writing it earlier would be a name sitting
   * beside a hint that has not caught up, which the store would ignore anyway,
   * and the ordering is worth more than the moment: two cookies written at one
   * point are two cookies that cannot fall out of step.
   *
   * In an effect rather than in the render body: assigning `document.cookie`
   * while rendering is a side effect, and under StrictMode's double render it
   * would happen twice for no reason.
   */
  useEffect(() => {
    if (isPending) return;
    if (!signedIn) {
      rememberSession(false);
      rememberName(null);
      return;
    }
    if (!rolesSettled) return;
    rememberSession({ signedIn, creator, admin });
    rememberName(name);
  }, [isPending, signedIn, rolesSettled, creator, admin, name]);

  /**
   * The routes that need a session are gated here, at the root, rather than one
   * by one — and, separately, every route waits for the session to be *known*.
   *
   * Two questions that look like one and are not. **Who may read this** is the
   * gate below and is a property of the path. **Do we yet know who is reading**
   * is `settling`, and is a property of the load: it is true on the storefront
   * as much as in the portal, because the bar at the top of both is drawn from
   * the answer. See `settling` for what conflating them cost.
   *
   * **The polarity of the gate flipped when the storefront moved in.** It used
   * to be "everything except the sign-in pages", because everything else was a
   * dashboard. Most of the application is public now, so it asks `needsSession`
   * — see the note there, and `APP_PATHS` in core, which is the list.
   *
   * `isPending` matters more than it did under Clerk. `useSession` asks the API
   * whether the stored token is still good, so there is a moment where we do
   * not know — rendering the redirect during it would sign out anybody
   * refreshing a page. Not rendering the application is the honest state.
   *
   * **What that state looked like was an empty fragment** (#100), and it is on
   * screen for a network round trip on *every* page load — so the first thing
   * anybody saw of this product, every time, was a blank rectangle. It is the
   * brand mark waiting now, in the colours of the page that is arriving, and it
   * holds itself back for a moment first so that a quick answer is still the
   * silent instant load it always was. See `loading.tsx`.
   *
   * **And it is a boot screen once the wait is long enough to be one** (#135):
   * the lockup, a bar sweeping the bottom edge, and a tip about GOODBUILD that
   * changes every few seconds until the session answers. Which tips is decided
   * by the same `currentSpace` call that decides the colours — a studio waiting
   * on its portal reads about builds and payouts, not about the refund button.
   *
   * The label is written here rather than defaulted in the component, for the
   * same reason the two redirects below carry theirs: this is the only place
   * that knows *which* wait this is, and `Loading` is what a component says
   * when nobody told it.
   *
   * The redirect does not hold anything back: the browser has already been told
   * to go somewhere and the wait is a whole page load, so there is nothing to
   * gain by waiting to say so.
   */
  const path = typeof window === 'undefined' ? '/' : window.location.pathname;

  /**
   * Whether we know enough about the reader to draw anything true yet.
   *
   * **This is not the same question as `needsSession`, and putting the boot
   * screen under that gate was the bug** (#135). `needsSession` asks *may this
   * path be read by a stranger*, which is false for `/` and every other public
   * page. The session check is asked on **every** load regardless — `useSession`
   * is called unconditionally at the top of this function — so on the storefront
   * the boot screen was never reached at all, and what a visitor actually saw
   * was the store home drawing itself with a signed-out bar and then correcting
   * it a round trip later.
   *
   * That correction is the thing worth waiting for. The chrome is *about* the
   * reader — whether there is an account link, whether Portal, Board and Admin
   * are in the bar — so a page drawn before the answer is a page that has to
   * rearrange itself in front of somebody.
   *
   * **`/me` is inside the wait as well as the session**, which is the second
   * half of the same fact: `isPending` clearing says *there is a person*, and
   * `rolesSettled` says *and this is what they can open*. Waiting for only the
   * first draws a bar with the account link and no role links, then adds them.
   * It costs a signed-out visitor nothing — `/me` is not fetched without a
   * session, so `signedIn` short-circuits — and it cannot hang the application
   * on a bad request, because `rolesSettled` is true on failure as well as on
   * success. See the note on it above.
   *
   * The authentication pages are the exception and have to be. Their whole job
   * is to *establish* a session, they read nothing from one, and holding the
   * sign-in form behind a check that is about to answer "nobody" is a round
   * trip charged to the person least able to spare it — somebody who already
   * knows they are signed out and has come here to fix it.
   */
  const settling = isPending || (signedIn && !rolesSettled);

  if (settling && !AUTH_PATHS.has(path)) {
    return (
      <Waiting
        space={currentSpace(pathname)}
        /*
          Which half of the wait this is. `isPending` is the token being
          checked; past it we have a person and are asking what they can open.
        */
        label={isPending ? 'Checking your session' : 'Getting your account ready'}
      />
    );
  }

  if (needsSession(path) && !signedIn) {
    const redirect = encodeURIComponent(path);
    window.location.href = `/sign-in?redirect=${redirect}`;
    return <Waiting space={currentSpace(pathname)} label="Taking you to sign in" now />;
  }

  /**
   * Signed in, and nothing works until the address is confirmed.
   *
   * **Rendered in place of the page rather than redirected to**, which is the
   * one decision here worth stating. A redirect would throw away the address
   * somebody was trying to reach and would have to be undone by hand after they
   * confirmed; this leaves the URL where it is, so pressing *I have confirmed
   * it* on the wall reloads into the page they wanted. It also cannot loop,
   * which a redirect on a gate this broad eventually does.
   *
   * `=== false` rather than falsy, deliberately. The session is `undefined`
   * until it lands, and treating *not yet known* as *not confirmed* would flash
   * this wall in front of everybody on every load — the same class of mistake
   * `settling` above exists to prevent.
   *
   * **The gate itself is `requireCaller` in the API**, which refuses every
   * route bar two for an unconfirmed caller. This is the explanation, not the
   * enforcement; a wall drawn only here would be a wall with a `fetch` through
   * it.
   *
   * It applies to the signed-in surface only. A public page — the store, a game,
   * the legal documents — stays readable, because none of it is *their account*
   * and a shop that hides its shelves from somebody halfway through joining has
   * misread what the rule is for.
   */
  const unconfirmed = signedIn && session?.user.emailVerified === false;
  const walled = unconfirmed && needsSession(path);

  return (
    <LinkProvider component={RouterLinkAdapter}>
      {/*
        No `OriginProvider`, and its absence is the point.

        It used to install the storefront's origin here, because this app
        renders the *store's* chrome — `resolveSite` answers `store` for this
        host — and `Games`, `Publishing`, `About` and most of the footer were
        paths on a Worker somewhere else. One deployment serves all of them now,
        so every one of those hrefs is correct relative to whatever host
        answered. The provider went with the resolver it fed; see the note where
        `chromeHref` used to be, in `apps/app/src/ui`'s `site.ts`.
      */}
      <AccountProvider
        signedIn={signedIn}
        account={signedIn ? <Account name={name ?? 'Account'} /> : undefined}
        standing={standing}
        rail={rail}
      >
        {/*
          Over the page rather than instead of it, and only once a navigation
          has been going long enough to be worth mentioning. Inside the
          providers so that a route rendered under it is unaffected by it being
          there. See `Navigating`.
        */}
        <Navigating space={currentSpace(pathname)} />
        {/*
          Inside the providers rather than returned early above, because the
          wall draws the wordmark and the wordmark is a `Link` — and a `Link`
          outside `LinkProvider` is the one component in this app that cannot
          render itself.
        */}
        {walled ? <VerifyEmailRoute /> : <Outlet />}
      </AccountProvider>
    </LinkProvider>
  );
}

/**
 * A path with no route, and the two URL shapes that only look like one.
 *
 * **This used to be mostly a forwarding desk.** The storefront was a separate
 * Worker, so `/browse` typed on this host was not a mistake — it was a real
 * page on the other origin — and most of this function decided which URLs to
 * hand over and which to refuse. One deployment serves everything now, so a
 * path with no route is simply a path with no route.
 *
 * Two things still arrive here that are not, and neither can be a registered
 * route:
 *
 * **`goodbuild.cc/@victor`**, the vanity creator URL. It cannot be a route
 * because `@` cannot start a TanStack path parameter and a bare `/$handle` at
 * the root would swallow every other top-level path. It was an Astro catch-all
 * that rewrote to `/c/<handle>`; here it renders that page directly, which is
 * better than a redirect — the URL a creator handed out stays in the bar.
 *
 * **`/developers` used to be forwarded here, and is not any more** (#142). It
 * was the pitch page under its old name — the copy on it changed from "list
 * your game with us" to "we publish games, apply to be one of them", and the
 * path was renamed to `/publishing` because `/developers` described an audience
 * rather than the thing being offered. The forward existed because studios had
 * that URL in bookmarks and email threads, and a 404 would have read as the
 * programme being withdrawn.
 *
 * The programme *is* withdrawn. There is no pitch page to forward to, and a
 * hop to a route this bundle no longer registers would land somebody on a
 * not-found by a slower road. So both paths fall through to `NotFound`, which
 * is the honest answer to a request for a page that does not exist.
 */
function NotHere(): React.ReactElement {
  const path = typeof window === 'undefined' ? '/' : window.location.pathname;

  /*
   * A handle, and the guard is what stops this swallowing real mistakes.
   *
   * Anything that is not `/@something` falls through to the not-found below,
   * which is the same rule the Astro catch-all had: `if (!path.startsWith('@'))
   * return Astro.rewrite('/404')`. A handle that names nobody is a 404 too, and
   * the creator page decides that for itself off the API's answer.
   */
  if (path.startsWith('/@') && path.length > 2) {
    return <CreatorPageRoute handle={decodeURIComponent(path.slice(2))} />;
  }

  return <NotFound />;
}

const rootRoute = createRootRoute({
  component: Shell,
  notFoundComponent: NotHere,
});

/**
 * The root, which is the shop on the apex and a workplace on the other two
 * hosts.
 *
 * **It used to redirect unconditionally**, on the reasoning that this app had
 * no home of its own: somebody arriving at `app.goodbuild.cc` came for a
 * specific tool, and a landing page would only ever be clicked through. That
 * was right while `goodbuild.cc` was the Astro storefront and this deployment
 * was the three dashboards. It is one deployment now and the apex lands here,
 * so the store rule became `/library` — a sign-in wall as the first thing every
 * first-time visitor met.
 *
 * The hostname still decides for the audience it was written for: somebody who
 * typed `creators.goodbuild.cc` came for the board, which is behind a session,
 * and that host is a workplace.
 *
 * **`developers.goodbuild.cc` no longer forwards anywhere** (#144). It sent
 * somebody to `/portal`, which is not a route: the portal was a studio's own
 * screens and there are no studios. The host falls through to the catalogue,
 * which is the honest answer — there is nothing else behind it.
 *
 * `beforeLoad` rather than a component that redirects, so the catalogue is
 * never rendered for a moment on a host that was going somewhere else.
 *
 * **The component is `BrowseRoute`, and there is no separate home page.** `/`
 * used to render `StoreHomeRoute`: the same shelf cut to twelve, under a hero,
 * with a rail linking to `/browse` for the rest. Two pages drawing one grid
 * from one query, differing by a limit. The catalogue is a studio's rather than
 * a marketplace's, so the shorter list was the whole list, and the front page
 * existed to point at the page that already showed everything. See `browse.tsx`.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (currentSite() === 'creators') throw redirect({ to: '/board' as never });
  },
  component: BrowseRoute,
});

/**
 * `/browse`, which is `/` now and stays reachable.
 *
 * The catalogue moved to the root rather than being renamed, so unlike
 * `/developers` — which was forwarded to a pitch page that then ceased to exist,
 * and is a plain 404 for that reason — this one has somewhere real to go. The
 * path is in the footer of everything printed before today, in whatever links
 * anybody has shared, and in a bookmark or two.
 *
 * A router redirect rather than a component that renders the same page: two URLs
 * serving one catalogue is two things for a search engine to weigh and two
 * answers to "where is the games page". `beforeLoad` means nothing is drawn on
 * the way through.
 */
const browseRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: '/browse',
  beforeLoad: () => {
    throw redirect({ to: '/' as never });
  },
});

const route = (path: string, component: () => React.ReactElement) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const routeTree = rootRoute.addChildren([
  indexRoute,

  /*
    The public half of the product, which was fifteen `.astro` pages on a
    separate Worker until this change.

    Registered first and grouped, so the file reads shop-then-workplace the way
    the site does. None of them appear in `APP_PATHS`, which is what leaves them
    outside the session gate — see `needsSession`. Adding a page here needs
    nothing anywhere else; adding one to `APP_PATHS` puts it behind a sign-in.
  */
  browseRedirect,
  route('/g/$slug', GameRoute),
  /*
    `/dev/$slug` and `/pub/$slug` were here, and went with the studios (#144).

    They were the two public profile pages: a studio's own "about us" beside a
    credibility block generated from its whole catalogue, and the publisher
    above it. A game page links to neither now — it prints the name and stops,
    because `product.developer_name` is a string and there is nothing behind it.
  */
  /*
    A creator's storefront, at the path a link uses. `goodbuild.cc/@victor` is
    the same page and is not a route — `@` cannot start a path parameter — so
    `NotHere` renders this component for it directly.
  */
  route('/c/$handle', () => <CreatorPageRoute />),
  route('/about', AboutRoute),
  /*
    The brand, public and deliberately so.

    A studio deciding whether to sign, a creator putting our code in a video and
    anybody writing about us all need to know what the mark is and what may be
    done with it, and none of them has an account. So it is not in `APP_PATHS`
    and there is no gate on it. `goodbuild-brand-guidelines.html` at the root of
    the repository is still the specification; this is the half of it somebody
    will actually read.
  */
  route('/brand', BrandRoute),
  route('/contact', ContactRoute),
  route('/creators', CreatorsRoute),
  /*
    Singular, and one character from `/downloads`, which is the launcher's own
    screen listing what it is installing. This is the page that offers the
    launcher itself, to somebody who has none.
  */
  route('/download', DownloadRoute),
  /*
    The three legal documents, which are one component with a kind passed in so
    that the presentation of the terms cannot drift from the presentation of the
    refund policy. The text is the same source the acceptance flow hashes.
  */
  route('/legal/terms', () => <LegalRoute kind="platform_terms" fallbackTitle="Terms" />),
  route('/legal/privacy', () => <LegalRoute kind="privacy_policy" fallbackTitle="Privacy" />),
  route('/legal/refunds', () => <LegalRoute kind="refund_policy" fallbackTitle="Refunds" />),

  route('/sign-in', SignInRoute),
  /*
    Five questions rather than a panel, and its own component rather than the
    sign-in with a flag. It asks for the terms, an age, a country and a route in
    before it asks for an address; see `./routes/sign-up`.
  */
  route('/sign-up', SignUpRoute),
  route('/forgot-password', ForgotPasswordRoute),
  route('/reset-password', ResetPasswordRoute),
  route('/verified', VerifiedRoute),
  /*
    Where signing up with an address ends, and the screen `Shell` draws in place
    of any page behind the session gate until the address is confirmed. One
    component, two ways to it — see `VerifyEmailRoute`.
  */
  route('/verify-email', VerifyEmailRoute),
  route('/account', AccountRoute),
  route('/bought', BoughtRoute),
  route('/library', LibraryRoute),
  route('/library/$slug', LibraryGameRoute),
  route('/orders', OrdersRoute),
  route('/board', BoardRoute),
  /*
    The whole `/portal` tree was here, and went with the studios (#144).

    Eight routes: `/portal` itself (a studio's games), `/portal/stats`,
    `/portal/builds`, `/portal/organisation` and its members, `/portal/profile`,
    `/portal/credits` and `/portal/credits/mine`, and `/portal/agreements`.
    `/portal/submit` had gone at #142 and `/portal/payouts` at #143.

    Every one of them was a screen a studio opened about its own company: what
    it had sold, what it had pushed, who could act for it, what it had signed.
    There is no company. The catalogue is ours and it is administered from
    `/admin`, which is where a title is created, priced, approved and released,
    and where the build tokens are issued.
  */
  route('/onboarding', OnboardingRoute),
  /*
    Which application, said by the URL rather than left to the hostname.

    `/onboarding` alone answers from the host, and this deployment answers three
    of them — so on `app.goodbuild.cc` it showed a studio registration to
    everybody, including somebody who had just pressed `Apply to the programme`
    on the creators pitch. Anything linking *at* this app from outside its own
    chrome names the one it means. See `requestedRole`.
  */
  route('/onboarding/creator', OnboardingRoute),
  route('/onboarding/studio', OnboardingRoute),
  route('/onboarding/legal', OnboardingRoute),
  /*
    `/onboarding/payouts` was here and #143 removed it. It named the payout step
    for whichever application the path said — and the studio half of that step
    is gone, so the surviving half is a creator connecting a provider account,
    which `/onboarding/creator` already reaches.
  */
  route('/admin', AdminRoute),
  /*
    `/admin/queue` was here: everything waiting on a decision, in one list
    (#112). Every source that fed it has since been closed — nothing registers
    itself, nothing is submitted to us, and the payout figure counted against a
    ledger that is gone — so it could only ever report an empty desk. See the
    note at the foot of `admin-queues.tsx`.
  */
  route('/admin/people', AdminPeopleRoute),
  route('/admin/people/$userId', AdminPersonRoute),
  /*
    `/admin/ledger` was here and went with the books. Creem is the record of
    money; there is no second set to check it against from inside this product.
  */
  route('/admin/games', AdminGamesRoute),
  /*
    The create, on its own page (#127).

    After the table rather than inside it, so `/admin/games` keeps meaning the
    catalogue. It is a step-by-step flow — the same one a studio fills in at
    `/submit`, asked of somebody typing on that studio's behalf — and a flow
    wants the width and the attention that a panel above a table cannot give it.
  */
  route('/admin/games/new', AdminNewTitleRoute),
  /*
    One title, from the panel (#136).

    After `/admin/games/new` and it has to be, in the sense that matters for
    reading rather than for matching: `new` is a literal and `$productId` is a
    parameter, so TanStack scores the literal higher whichever order they are
    registered in and `/admin/games/new` can never be read as a title whose id
    is the word "new". Written in this order anyway, because a reader should not
    have to know that to be sure.

    It is the screen editing a game used to have no address for at all — the
    editor was a panel that grew inside a row of the table above, so there was
    nothing to link to, nothing to bookmark, and a Back that closed the panel
    rather than leaving the title.
  */
  route('/admin/games/$productId', AdminGameRoute),
  /*
    `/admin/agreements` and `/admin/contracts` were here, and went with the
    studios (#144). The first was every publishing agreement and where each had
    got to; the second was the blank paper they were drawn from. Both described
    a contract between GOODBUILD and another company.
  */
  /*
    `/admin/publishing` was here, and #142 removed it with the form that filled
    it. It was the inbox for pitches sent from `goodbuild.cc/publishing` — a
    queue that can now only ever be empty, and a desk screen that is always
    empty is one everybody learns to stop opening.
  */
  /*
    `/admin/curation` was here. A title is still curated, and the decision is
    taken on its own page — `/admin/games/:productId` — rather than from a row
    in a list of things waiting.
  */
  /*
    `/admin/studios` and `/admin/studios/$developerId` were here, and went with
    the studios (#144). The first was every studio there was and the ones
    waiting to be verified; the second stood in for a studio's own portal — its
    details, its people, its build tokens — for the studios that had nobody to
    open one. The build tokens moved to `/admin/tokens`, because a build still
    has to be uploaded.
  */
  route('/admin/tokens', AdminTokensRoute),
  route('/admin/gifts', AdminGiftsRoute),
  route('/admin/creators', CreatorQueueRoute),
  route('/admin/refunds', RefundQueueRoute),
  /*
    `/admin/payouts` was here. It reported how many creator payout recipients
    could not be paid yet, counted off the ledger, and both the count and the
    ledger are gone.
  */

  /*
    The two screens that exist only on the desktop, and only there.

    This bundle is served twice — by Cloudflare to a browser, and by Tauri's
    asset protocol to the launcher's webview — so these cannot simply be routes
    like the rest. Registering them unconditionally would give a web player two
    entries that render a page whose every button calls a bridge that is not
    there; spreading nothing in a browser means the router genuinely has no such
    path, and `NotFound` hands `/downloads` to the storefront the same way it
    handles any other path this app does not serve.

    `isLauncher()` is read once at module load rather than per render — see
    `is-launcher.ts` for why that is safe rather than lucky, and why the
    question is asked of the IPC bridge rather than of the user-agent string.

    Deliberately **not** added to `PUBLIC_PATHS`: both belong behind the session
    gate like every other screen here. Both also refuse on their own if reached
    without a bridge, so a mistake in this list renders a sentence rather than a
    page that throws on contact.
  */
  ...(isLauncher()
    ? [route('/downloads', DownloadsRoute), route('/settings/launcher', LauncherSettingsRoute)]
    : []),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });
export { currentSite, currentSpace };

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
